import { createContext, useState, useEffect, useMemo, ReactNode } from 'react'
import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { SUPABASE_ENVIRONMENTS, EnvironmentId, SupabaseEnvironment, getAvailableEnvironments } from '@/config/supabase-environments'
import { useToast } from '@/components/ui/use-toast'

interface SupabaseEnvironmentContextValue {
  currentEnv: EnvironmentId
  currentClient: SupabaseClient
  currentConfig: SupabaseEnvironment
  switchEnvironment: (env: EnvironmentId) => Promise<void>
  isReady: boolean
  isLoading: boolean
  availableEnvironments: EnvironmentId[]
}

export const SupabaseEnvironmentContext = createContext<SupabaseEnvironmentContextValue | null>(null)

interface SupabaseEnvironmentProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'admin_supabase_env'

function getInitialEnvironment(): EnvironmentId {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && (stored === 'dev' || stored === 'prod')) {
    return stored as EnvironmentId
  }
  return 'dev' // fallback
}

export function SupabaseEnvironmentProvider({ children }: SupabaseEnvironmentProviderProps) {
  // Restore environment from localStorage, default to 'dev' if not set
  const [currentEnv, setCurrentEnv] = useState<EnvironmentId>(getInitialEnvironment)
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const { toast } = useToast()

  // Get list of environments with valid credentials
  const availableEnvironments = useMemo(() => getAvailableEnvironments(), [])

  // Create Supabase clients for both environments (lazy, memoized)
  const clients = useMemo(() => {
    const clientMap: Partial<Record<EnvironmentId, SupabaseClient>> = {}

    availableEnvironments.forEach((envId) => {
      const env = SUPABASE_ENVIRONMENTS[envId]
      if (env.url && env.anonKey) {
        clientMap[envId] = createClient(env.url, env.anonKey)
      }
    })

    return clientMap
  }, [availableEnvironments])

  // Get current client and config
  const currentClient = useMemo(() => {
    const client = clients[currentEnv]
    if (!client) {
      console.error(`No client available for environment: ${currentEnv}`)
      // Fallback to dev client or first available
      return clients.dev || Object.values(clients)[0]!
    }
    return client
  }, [currentEnv, clients])

  const currentConfig = useMemo(() => SUPABASE_ENVIRONMENTS[currentEnv], [currentEnv])

  // Initialize - always start in dev
  useEffect(() => {
    // Ensure we have at least dev environment configured
    if (!availableEnvironments.includes('dev')) {
      console.error('Dev environment not configured!')
      toast({
        title: 'Configuration Error',
        description: 'Dev environment is not properly configured',
        variant: 'destructive',
      })
    }
    setIsReady(true)
  }, [availableEnvironments, toast])

  // Persist current environment to localStorage for restoration on page refresh
  useEffect(() => {
    if (isReady) {
      localStorage.setItem(STORAGE_KEY, currentEnv)
    }
  }, [currentEnv, isReady])

  // Switch environment with validation
  const switchEnvironment = async (targetEnv: EnvironmentId) => {
    if (targetEnv === currentEnv) {
      return // Already in target environment
    }

    // Check if target environment is available
    if (!availableEnvironments.includes(targetEnv)) {
      toast({
        title: 'Environment Not Available',
        description: `${SUPABASE_ENVIRONMENTS[targetEnv].label} environment is not configured`,
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)

    try {
      const newClient = clients[targetEnv]
      if (!newClient) {
        throw new Error(`Client not available for ${targetEnv}`)
      }

      // Test connection with a simple query
      const { error } = await newClient
        .from('agents')
        .select('id')
        .limit(1)

      if (error) {
        throw error
      }

      // Success - commit the switch
      setCurrentEnv(targetEnv)

      toast({
        title: 'Environment Switched',
        description: `Now connected to ${SUPABASE_ENVIRONMENTS[targetEnv].label} (${SUPABASE_ENVIRONMENTS[targetEnv].region})`,
      })
    } catch (error) {
      console.error('Failed to switch environment:', error)
      toast({
        title: 'Switch Failed',
        description: `Failed to connect to ${SUPABASE_ENVIRONMENTS[targetEnv].label} environment`,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const value: SupabaseEnvironmentContextValue = {
    currentEnv,
    currentClient,
    currentConfig,
    switchEnvironment,
    isReady,
    isLoading,
    availableEnvironments,
  }

  return (
    <SupabaseEnvironmentContext.Provider value={value}>
      {children}
    </SupabaseEnvironmentContext.Provider>
  )
}
